import { getCookieDurationMap } from '../Config/CookiesData';

// Not: Hooks/useCookieConsent dosyanızdaki CookiePreferences tipinin bu şekilde olduğunu varsayıyoruz.
// import { CookiePreferences } from 'Hooks/useCookieConsent';
export interface CookiePreferences {
  essential: boolean;
  performance: boolean;
  marketing: boolean;
}

const GOOGLE_ANALYTICS_ID = 'G-R5HJZMWTJ7'; 
const MICROSOFT_CLARITY_ID = 'j0o5qnmous'; 

const WHITELIST_ESSENTIAL = [
  'apiGWToken', 'apiGWRefreshToken', 'BIGipServerpool_media.istanbulkart.istanbul_80',
  'TS015054be', 'TS015dd880', 'APISID', 'SAPISID', 'SID', 'SSID', 'HSID', 
  'SIDCC', 'AEC', 'SEARCH_SAMESITE', '__Secure-1PAPISID', '__Secure-1PSID', 
  '__Secure-1PSIDCC', '__Secure-1PSIDTS', '__Secure-3PAPISID', '__Secure-3PSID', 
  '__Secure-3PSIDCC', '__Secure-3PSIDTS'
];
const WHITELIST_PERFORMANCE = ['_ga', '_ga_GEXVL6ETTY', '_ga_R5HJZMWTJ7', '_clck', '_clsk'];
const WHITELIST_MARKETING = ['NID', 'Secure-BUCKET', 'MUID', 'MR', 'SRM_B', 'ANONCHK', 'CLID', 'SM'];

let watchdogInterval: NodeJS.Timeout | null = null;

export const applyCookiePreferences = (preferences: CookiePreferences) => {
  manageScripts(preferences);

  if (watchdogInterval) clearInterval(watchdogInterval);
  purgeUnknownCookies(preferences);

  // Düzenli temizleyiciyi tekrar kur
  watchdogInterval = setInterval(() => {
    purgeUnknownCookies(preferences);
  }, 1000); 
};

const manageScripts = (preferences: CookiePreferences) => {
  if (typeof window === 'undefined') return;

  const gaProperty = `ga-disable-${GOOGLE_ANALYTICS_ID}`;

  // 1. Performans: Google Analytics — yalnızca performans rızası yeterlidir.
  if (preferences.performance) {
    (window as any)[gaProperty] = false;
    loadGoogleAnalytics();
  } else {
    // Kapatıldıysa Google Analytics'i globalden engelle ve çerezlerini sil
    (window as any)[gaProperty] = true;
    deleteCookie('_ga');
    deleteCookie('_ga_GEXVL6ETTY');
    deleteCookie('_ga_R5HJZMWTJ7');
  }

  // 2. Microsoft Clarity — paket hem performans (_clck/_clsk) hem pazarlama
  // (ANONCHK/CLID/SM) çerezi tetiklediğinden, Çerez Politikası Bölüm 6 gereği
  // yalnızca PERFORMANS ve PAZARLAMA açık rızalarının İKİSİ de verildiğinde çalıştırılır.
  // Böylece pazarlama reddedilmişken Clarity'nin çerezlerini saniyede bir silme (churn) önlenir.
  if (MICROSOFT_CLARITY_ID && preferences.performance && preferences.marketing) {
    loadClarity();
  } else {
    deleteCookie('_clck');
    deleteCookie('_clsk');
  }

  // 3. Pazarlama İzinleri (Google, Bing, Microsoft vb.)
  // Not: ANONCHK / CLID / SM, nihai Çerez Politikası'nda (Bölüm 6 + Ek-1) pazarlama
  // kategorisinde sınıflandırıldığı için pazarlama rızası yoksa silinir.
  if (!preferences.marketing) {
    deleteCookie('NID');
    deleteCookie('Secure-BUCKET');
    deleteCookie('MUID');
    deleteCookie('MR');
    deleteCookie('SRM_B');
    deleteCookie('ANONCHK');
    deleteCookie('CLID');
    deleteCookie('SM');
  }
};

const isCookieWhitelisted = (cookieName: string, prefs: CookiePreferences): boolean => {
  if (!cookieName) return false;
  
  // Essential List ve Prefix kontrolü
  if (WHITELIST_ESSENTIAL.includes(cookieName) || cookieName.startsWith('BIGip') || cookieName.startsWith('TS01')) {
    return true;
  }
  
  // Performance List ve Prefix kontrolü
  if (prefs.performance && (WHITELIST_PERFORMANCE.includes(cookieName) || cookieName.startsWith('_ga_'))) {
    return true;
  }
  
  // Marketing List kontrolü
  if (prefs.marketing && WHITELIST_MARKETING.includes(cookieName)) {
    return true;
  }
  
  return false;
};

const purgeUnknownCookies = (prefs: CookiePreferences) => {
  if (typeof document === 'undefined') return;
  const allCookies = document.cookie.split(';');

  allCookies.forEach((cookie) => {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName && !isCookieWhitelisted(cookieName, prefs)) {
      deleteCookie(cookieName);
    }
  });
};

const deleteCookie = (name: string) => {
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  
  // Üst domain, alt domain ve 3. taraf servisler (tarayıcı izin verirse)
  const domains: string[] = ['', hostname, `.${hostname}`];
  if (domainParts.length > 2) {
    domains.push(`.${domainParts.slice(-2).join('.')}`);
  }
  domains.push('.clarity.ms', '.bing.com', '.google.com');

  const paths = ['/', '', '/bireysel', '/b2c'];

  domains.forEach((domain) => {
    paths.forEach((path) => {
      const pathAttr = path ? `; path=${path}` : '';
      const domainAttr = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathAttr}${domainAttr}`;
    });
  });
};

/**
 * Çerez yazarken duration'ı otomatik olarak hesaplayarak expires header'ı ekler
 * Duration mapping CookiesData.ts'den gelir ve modal'daki süreler ile tutarlı
 */
export const setCookieWithDuration = (name: string, value: string, domain?: string, path: string = '/') => {
  if (typeof document === 'undefined') return;
  
  const durationMap = getCookieDurationMap();
  const durationMs = durationMap[name] || 0;
  
  let cookieString = `${name}=${value}`;
  if (domain) cookieString += `; domain=${domain}`;
  cookieString += `; path=${path}`;
  
  // Session cookies (duration === 0) için expires set etmiyoruz
  if (durationMs > 0) {
    const expiresDate = new Date(Date.now() + durationMs);
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }

  if (window.location.protocol === 'https:') {
    cookieString += '; Secure; SameSite=Lax';
  } else {
    cookieString += '; SameSite=Lax';
  }
  
  document.cookie = cookieString;
};

const loadGoogleAnalytics = () => {
  if (document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);

  const inlineScript = document.createElement('script');
  inlineScript.id = 'ga-init';
  inlineScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GOOGLE_ANALYTICS_ID}', { 'anonymize_ip': true });
  `;
  document.head.appendChild(inlineScript);
};

const loadClarity = () => {
  if (document.getElementById('clarity-script')) return;
  const script = document.createElement('script');
  script.id = 'clarity-script';
  script.type = 'text/javascript';
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${MICROSOFT_CLARITY_ID}");
  `;
  document.head.appendChild(script);
};

export const hasCookie = (name: string): boolean => {
  return document.cookie.split(';').some((item) => item.trim().startsWith(`${name}=`));
};